import { z } from 'zod'

/**
 * Image type enumeration for categorizing image-based flashcards
 */
export const ImageTypeSchema = z.enum(['ACTION', 'EMOTION', 'OBJECT'])
export type ImageType = z.infer<typeof ImageTypeSchema>

/**
 * Schema for card input from user
 * Can be image-based, text-based, or both
 */
export const CardInputSchema = z.object({
  image: z.instanceof(File).optional(),
  text: z.string().trim().min(1, "Text must not be empty or whitespace").optional()
}).refine(
  (data) => data.image !== undefined || data.text !== undefined,
  { message: "Either 'image' or 'text' must be provided" }
)
export type CardInput = z.infer<typeof CardInputSchema>

/**
 * Schema for AI-generated flashcard data
 * Contains the front, back, definition, example, part of speech,
 * collocations, tags, and image type
 */
export const GeneratedCardSchema = z.object({
  front: z.string().min(1, "Front text is required"),
  back: z.string().min(1, "Back translation is required"),
  definition: z.string().min(1, "Definition is required"),
  example: z.string().min(1, "Example sentence is required"),
  pos: z.string().min(1, "Part of speech is required"),
  phonetic_us: z.string().default(''),
  phonetic_uk: z.string().default(''),
  collocations: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  type: ImageTypeSchema
})
export type GeneratedCard = z.infer<typeof GeneratedCardSchema>

/**
 * Response type for multi-card generation
 * AI returns 1-5 cards per request, mixing words/phrases/expressions as context dictates
 */
export interface GeneratedCardsResponse {
  cards: GeneratedCard[]
}

/**
 * Schema for Spaced Repetition System (SRS) metadata
 * Contains next review date, interval, ease factor, and review count
 */
export const CardMetadataSchema = z.object({
  nextReviewDate: z.date(),
  interval: z.number().int().nonnegative("Interval must be non-negative"),
  easeFactor: z.number().min(1.3, "Ease factor must be at least 1.3"),
  reviewCount: z.number().int().nonnegative("Review count must be non-negative")
})
export type CardMetadata = z.infer<typeof CardMetadataSchema>

/**
 * Schema for card response from API
 * Matches the database Card interface
 */
export const CardResponseSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  front: z.string(),
  back: z.string(),
  definition: z.string(),
  example: z.string(),
  pos: z.string(),
  phonetic_us: z.string().default(''),
  phonetic_uk: z.string().default(''),
  type: z.string(),
  image_key: z.string().nullable(),
  tags: z.array(z.string()).default([]),
  created_at: z.string()
})
export type CardResponse = z.infer<typeof CardResponseSchema>

/**
 * Schema for card response with SRS review data
 * Used by review sessions to display due cards
 */
export const CardWithSRSSchema = CardResponseSchema.extend({
  next_review_at: z.string(),
  current_ease_factor: z.number(),
  current_interval_days: z.number(),
  total_reviews: z.number(),
  correct_reviews: z.number()
})
export type CardWithSRS = z.infer<typeof CardWithSRSSchema>

/**
 * Schema for card update request
 * All fields are optional for partial updates
 */
export const CardUpdateSchema = z.object({
  front: z.string().trim().min(1).optional(),
  back: z.string().trim().min(1).optional(),
  definition: z.string().trim().min(1).optional(),
  example: z.string().trim().min(1).optional(),
  pos: z.string().trim().min(1).optional(),
  phonetic_us: z.string().trim().optional(),
  phonetic_uk: z.string().trim().optional(),
  type: z.string().trim().min(1).optional(),
  imageKey: z.string().nullable().optional()
})
export type CardUpdate = z.infer<typeof CardUpdateSchema>
